/**
 * Aggancio del frontend al monitor della flotta, col DSN preso A RUNTIME.
 *
 * Da copiare accanto al frontend che serve, come si fa con `sickbay_client.py` per i
 * servizi Python. Nessuna dipendenza: la libreria del monitor la passa chi chiama, cosi'
 * questo file va bene con Vue, React o niente.
 *
 * PERCHE' NON AL BUILD
 * Un DSN compilato nel bundle e' lo stesso per tutte le installazioni che ricevono quel
 * pacchetto. Il `dist` di GLEEM One va anche su CEDDIAL, l'immagine del Pit gira sia sul
 * dev sia sulla macchina del cliente: un DSN dentro il bundle manderebbe gli errori del
 * browser degli utenti del cliente nel progetto di casa nostra. E non se ne accorgerebbe
 * nessuno, perche' gli eventi arrivano e sembrano nostri.
 *
 * La regola completa: sickbay/docs/DSN-A-RUNTIME.md
 *
 * ── COPIA, NON SORGENTE ──────────────────────────────────────────────────────
 * Questo file arriva dal repo Sickbay (`clients/sickbay_frontend_runtime.ts`,
 * commit bdf729c) ed e' copiato qui come si fa col mini-client Python. Se va
 * corretto, si corregge LA', poi si ricopia: due copie che divergono in
 * silenzio sono peggio di una copia sola.
 */

/** Quello che il backend risponde su `GET /api/public/config` (rotta pubblica). */
export interface ConfigPubblica {
  /** vuoto o assente = canale spento. Non e' un errore, e' una configurazione. */
  sickbay_dsn?: string | null
  environment?: string | null
  release?: string | null
}

export interface OpzioniAggancio {
  /** Rotta pubblica del PROPRIO backend. Deve rispondere senza sessione. */
  url?: string
  /** Quanto si aspetta prima di rinunciare. Un monitor non fa aspettare nessuno. */
  timeoutMs?: number
  /**
   * L'inizializzazione vera, con la libreria che usa questo frontend. Viene chiamata
   * SOLO se e' arrivato un DSN. Esempio con Vue:
   *
   *   agganciaMonitor({ init: (c) => Sentry.init({ app, dsn: c.sickbay_dsn!,
   *     environment: c.environment ?? 'production', release: c.release ?? undefined,
   *     sendDefaultPii: false, tracesSampleRate: 0.1 }) })
   */
  init: (config: Required<Pick<ConfigPubblica, 'sickbay_dsn'>> & ConfigPubblica) => void
}

/**
 * Chiede la configurazione al proprio backend e, se c'e' un DSN, accende il monitor.
 *
 * Non solleva mai. Qualunque cosa vada storta .. rete assente, backend che non risponde,
 * rotta che non esiste ancora, risposta illeggibile .. il monitor semplicemente non si
 * accende e l'applicazione parte identica a prima. Un monitor che non parte non deve mai
 * impedire a un utente di lavorare: e' la stessa regola del mini-client Python.
 *
 * Va chiamata PRIMA del mount, cosi' copre anche gli errori del primo render.
 * Torna `true` se il monitor e' stato acceso: utile solo per i test.
 */
export async function agganciaMonitor(opzioni: OpzioniAggancio): Promise<boolean> {
  const url = opzioni.url ?? '/api/public/config'
  const timeoutMs = opzioni.timeoutMs ?? 3000

  let config: ConfigPubblica
  try {
    const stop = new AbortController()
    const timer = setTimeout(() => stop.abort(), timeoutMs)
    try {
      const risposta = await fetch(url, { signal: stop.signal, credentials: 'omit' })
      if (!risposta.ok) return false
      config = (await risposta.json()) as ConfigPubblica
    } finally {
      clearTimeout(timer)
    }
  } catch {
    // rete, timeout, rotta assente: il canale resta spento e non si dice niente a nessuno
    return false
  }

  const dsn = (config.sickbay_dsn ?? '').trim()
  if (!dsn) return false

  try {
    opzioni.init({ ...config, sickbay_dsn: dsn })
    return true
  } catch {
    // se la libreria del monitor esplode all'avvio, l'applicazione deve partire lo stesso
    return false
  }
}
